import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { apiClient, extractErrorMessage } from '@/lib/api';
import type { ImportReport } from '@/components/excel/ExcelToolbar';
import * as XLSX from 'xlsx';


export default function VehicleImportPage() {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [report, setReport] = useState<ImportReport | null>(null);
  // Which side of the business this file belongs to. A row with no division
  // does not show up inside NEOM, Dumpers or Flatbed, so this is asked for
  // up front rather than left to be fixed afterwards.
  const [projectCode, setProjectCode] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleFile(file: File) {
    setFileName(file.name);
    setReport(null);
    setError(null);
    setPendingFile(file);
    // Parsed only for the preview. The file itself is uploaded, so the sheet
    // keeps whatever headings it already has.
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        setParsedRows(XLSX.utils.sheet_to_json(sheet, { defval: null }));
      } catch {
        setParsedRows([]);
        setError('That file could not be opened as a spreadsheet.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function downloadTemplate() {
    setError(null);
    try {
      const res = await apiClient.get('/api/v1/excel/vehicles/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jasrhub-vehicles-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  async function handleSubmit() {
    if (!pendingFile) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const qs = `?partial=1${projectCode ? `&project=${encodeURIComponent(projectCode)}` : ''}`;
      const { data } = await apiClient.post<ImportReport>(`/api/v1/excel/vehicles/import${qs}`, fd);
      setReport(data);
    } catch (e) {
      // This used to throw into nothing, leaving the screen looking unchanged.
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background flex flex-col" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Logo size={28} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-base leading-none">Bulk Import Vehicles</div>
              <div className="text-[11px] text-muted-foreground">Upload Excel/CSV — preview — commit</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8 space-y-6">
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-3">Step 1 — Get the template</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Your own sheet works too, with the headings it already has — a plate written
            <b> 1835 BEA</b> is understood as <b>1835-BEA</b>, and a truck already in the system is
            matched on its plate and updated rather than added twice.
          </p>
          <Button variant="outline" onClick={downloadTemplate} className="gap-2">
            <Download className="h-4 w-4" /> Download template
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-3">Step 2 — Upload your file</h2>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => e.target.files && handleFile(e.target.files[0])}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:bg-muted/30 transition-colors"
          >
            <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            {fileName ? (
              <div>
                <div className="font-medium">{fileName}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {parsedRows.length} rows parsed — click to choose a different file
                </div>
              </div>
            ) : (
              <div>
                <div className="font-medium">Click to upload Excel or CSV</div>
                <div className="text-sm text-muted-foreground mt-1">.xlsx, .xls, .csv</div>
              </div>
            )}
          </div>
        </div>

        {parsedRows.length > 0 && !report && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg mb-3">Step 3 — Preview first 5 rows</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    {Object.keys(parsedRows[0] ?? {}).map((k) => (
                      <th key={k} className="px-2 py-2 text-start font-medium">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      {Object.values(r).map((v: any, j) => (
                        <td key={j} className="px-2 py-2 truncate max-w-[150px]">{v ?? '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Which side do these vehicles belong to?</span>
              <select
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="">Not set</option>
                <option value="PRJ-NEOM">NEOM</option>
                <option value="PRJ-RIYADH-WATER">Riyadh Water</option>
                <option value="PRJ-DUMPERS">Dumpers</option>
                <option value="PRJ-RIYADH">Flatbed</option>
              </select>
            </label>
            {!projectCode && (
              <p className="mt-2 text-xs text-amber-600">
                Left unset, these vehicles are filed under no division and will not appear inside
                NEOM, Dumpers or Flatbed.
              </p>
            )}
            <Button
              onClick={handleSubmit}
              disabled={busy}
              className="mt-4 gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import {parsedRows.length} vehicle(s)
            </Button>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Nothing was imported.</div>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}

        {report && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg mb-3 flex items-center gap-2">
              {report.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              {report.ok
                ? `Imported - ${report.toCreate} added, ${report.toUpdate} updated`
                : 'Nothing was imported - the file was refused'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
              <Stat label="Rows read" value={report.totalRows} />
              <Stat label="Added" value={report.toCreate} className="text-emerald-600" />
              <Stat label="Updated" value={report.toUpdate} className="text-sky-600" />
              <Stat label="Already correct" value={report.unchanged} />
              <Stat label="Set aside" value={report.skipped ?? 0} className={(report.skipped ?? 0) > 0 ? 'text-amber-600' : ''} />
            </div>
            {(report.matchedExisting ?? 0) > 0 && (
              <p className="text-sm rounded border border-sky-500/40 bg-sky-500/10 p-3 text-sky-700 mb-3">
                <b>{report.matchedExisting} were already in the system</b> and were updated rather
                than added again, matched on {report.matchedOn ?? 'their identifier'}.
              </p>
            )}
            {(report.ignoredColumns?.length ?? 0) > 0 && (
              <p className="text-sm rounded border border-border bg-muted/30 p-3 text-muted-foreground mb-3">
                Columns passed over because this system has nowhere to put them:{' '}
                <b className="text-foreground">{report.ignoredColumns!.join(', ')}</b>. Everything
                else in the file was read.
              </p>
            )}
            {report.errors.length > 0 && (
              <details className="mt-4" open>
                <summary className="cursor-pointer font-medium text-sm">
                  {report.errors.length} problem(s) - nothing was saved
                </summary>
                <ul className="mt-2 text-sm space-y-1">
                  {report.errors.slice(0, 100).map((e, i) => (
                    <li key={i} className="text-muted-foreground"><strong>Row {e.row} - {e.column}:</strong> {e.message}</li>
                  ))}
                </ul>
              </details>
            )}
            {(report.skippedRows?.length ?? 0) > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer font-medium text-sm">
                  {report.skippedRows!.length} row(s) set aside - finish these by hand
                </summary>
                <ul className="mt-2 text-sm space-y-1">
                  {report.skippedRows!.slice(0, 100).map((s, i) => (
                    <li key={i} className="text-muted-foreground"><strong>Row {s.row}:</strong> {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase">{label}</div>
      <div className={`text-2xl font-display ${className ?? ''}`}>{value}</div>
    </div>
  );
}
